<template>
  <Grid variant="detail" class="article-footer" role="contentinfo">
    <InfoBlock v-if="!forumTopic" v-bind="infoBlockData" class="article-footer-info-block" />
    <PageReplies v-else :topic="forumTopic" />

    <div v-if="state.dossiers && state.dossiers.length > 0" class="article-footer-dossiers">
      <div class="article-footer-dossiers-title">Meer in het Dossier</div>
      <div class="article-footer-dossiers-list">
        <template v-if="state.dossiers">
          <Button
            v-for="dossier in state.dossiers"
            :key="dossier.label || dossier.title || ''"
            variant="primary"
            size="small"
            :label="dossier.label || dossier.title || ''"
            :to="`/dossiers/${dossier?.slug}`"
            icon="eenvandaag-general:copy-04"
            :analytics="{ linkAnalyticsTag: 'article-more-dossiers' }"
          />
        </template>
      </div>
    </div>

    <div
      v-if="state.themes?.length > 0 || state.tags?.length > 0 || state.articleType"
      class="article-footer-tags"
    >
      <div class="article-footer-tags-title">Meer over</div>
      <div class="article-footer-tags-list">
        <template v-if="state.themes">
          <Button
            v-for="theme in state.themes"
            :key="theme.title || ''"
            variant="secondary"
            size="small"
            :label="theme.title || ''"
            :to="`${prefix}/themas/${theme.slug}`"
            :icon="theme.icon"
            :analytics="{ linkAnalyticsTag: 'article-more-about' }"
          />
        </template>
        <template v-if="state.articleType">
          <Button
            :key="state.articleType.title"
            variant="secondary"
            size="small"
            :label="state.articleType.title || ''"
            :to="state.articleType.slug"
            :icon="state.articleType.icon"
            :analytics="{ linkAnalyticsTag: 'article-more-about' }"
          />
        </template>
        <template v-if="state.tags">
          <Button
            v-for="tag in state.tags"
            :key="tag.title || ''"
            variant="secondary"
            size="small"
            :label="tag.title || ''"
            :to="`/tags/${tag.slug}`"
            :analytics="{ linkAnalyticsTag: 'article-more-about' }"
          />
        </template>
      </div>
    </div>
  </Grid>
</template>

<script setup lang="ts">
import type {
  ICmsArticlesTags,
  ICmsArticlesThemes,
  ICmsDossiersArticles,
} from '~~/types/_cms-types';
import type { IButton } from '~~/types/local_types';

interface IProps {
  tags?: ICmsArticlesTags[];
  themes?: ICmsArticlesThemes[];
  dossiers?: ICmsDossiersArticles[];
  type?: string | undefined;
  forumTopic?: number;
}

const props = defineProps<IProps>();
const prefix = props.type === 'opiniepanel/uitslagen' ? '/opiniepanel' : '';

const state = reactive({
  tags: props.tags?.map((tags) => tags.tagsId!) || [],
  themes: props.themes?.map((theme) => theme.themesId!) || [],
  dossiers: props.dossiers?.map((dossier) => dossier.dossiersId!) || [],
  articleType: useArticleType(props.type),
});

const infoBlockData = {
  title: 'Vragen? Stel ze!',
  text: {
    blocks: [
      {
        id: '4gUjU8LDmu',
        type: 'paragraph',
        data: {
          text: 'Heb je nog vragen of wil je reageren? Stuur ons een berichtje via de chat. ',
        },
      },
    ] as never[],
  },
  button: { label: 'Naar de chat', to: '/chat' } as IButton,
};
</script>

<style scoped lang="postcss">
.article-footer-info-block {
  --article-footer-info-block-margin: 0 0 var(--unit-12) 0;

  margin: var(--article-footer-info-block-margin);

  @media (--lg-viewport) {
    --article-footer-info-block-margin: 0 0 var(--unit-24) 0;
  }
}

.article-footer-dossiers {
  margin-bottom: var(--unit-6);

  @media (--lg-viewport) {
    margin-bottom: var(--unit-4);
  }
}

.article-footer-dossiers,
.article-footer-tags {
  --article-footer-dossiers-tags-gap: var(--unit-8);

  display: flex;
  flex-direction: column;
  gap: var(--article-footer-dossiers-tags-gap);
}

.article-footer-dossiers-list,
.article-footer-tags-list {
  --article-footer-dossiers-tags-list-gap: var(--unit-8);

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--article-footer-dossiers-tags-list-gap);
}

.article-footer-dossiers-title,
.article-footer-tags-title {
  color: hsl(var(--color-primary-700));
  padding-bottom: var(--unit-4);
  @add-mixin heading-sm-caps;
}
</style>
